// src/components/VoiceMicButton.jsx
// Reusable voice-to-text control for any text/textarea field across the Visit
// Workflow stages. Records with useSegmentedRecorder (auto-splits long
// recordings into <=25s chunks, since Sarvam rejects clips over 30s),
// transcribes each chunk via the same Sarvam endpoint used by Intake and
// concatenates them, auto-translates non-English speech to English (since
// stage forms/records are all kept in English), then hands the resulting
// text back to the caller via onText — the caller decides whether to replace
// or append to the field's current value.
//
// Speech language: read once from the tenant's admin-configured default
// (see lib/voiceLanguages.ts) rather than a per-button picker — every mic
// across the app now speaks the one language Admin Panel -> Branding ->
// Voice input sets, instead of each field carrying its own tiny dropdown.
import { useState } from 'react';
import { authFetch } from '../api';
import { useSegmentedRecorder } from '../hooks/useSegmentedRecorder';
import { transcribeSegments } from '../utils/sarvam';
import { getVoiceLanguage } from '../lib/voiceLanguages';
import { useAiAvailability } from '../features/ai-assist/useAiAvailability';

interface VoiceMicButtonProps {
  onText?: (text: string) => void;
  title?: string;
  /**
   * Translate non-English speech to English before handing it back (default true —
   * forms and records are kept in English). The ClintoBytes Copilot passes false so
   * a question spoken in Tamil goes in as spoken and the answer comes back in Tamil.
   */
  translateToEnglish?: boolean;
}

export default function VoiceMicButton({ onText, title, translateToEnglish = true }: VoiceMicButtonProps) {
  // AI-off (docs/HMS_V4_AI_PROVIDER_MANAGEMENT.md §4.4): this one leaf
  // component is reused across ~20 call sites (Verify, Register, Vitals,
  // Pre-Consult, Resolve, every IP Case Sheet section, Consultation,
  // prescription rows), so gating it here hides every mic in the app at
  // once rather than requiring each caller to remember to wrap it.
  const { loading, aiEnabled } = useAiAvailability();
  const [processing, setProcessing] = useState(false);
  const [error, setError]         = useState('');
  const { recording, start, stopAndWait } = useSegmentedRecorder();

  if (loading || !aiEnabled) return null;

  const handleStart = async () => { setError(''); await start(); };

  const handleStop = async () => {
    const segments = await stopAndWait();
    if (!segments.length) return;
    setProcessing(true); setError('');
    try {
      const lang = getVoiceLanguage();
      let text = await transcribeSegments(segments, lang, 'stage-voice-input');
      if (!text) { setError('No speech detected — try again'); setProcessing(false); return; }
      if (translateToEnglish && lang !== 'en-IN') {
        const tRes  = await authFetch('/api/voice/translate', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, source_language_code: lang, target_language_code: 'en-IN' }),
        });
        const tData = await tRes.json();
        if (tRes.ok) text = tData.translated_text;
      }
      onText?.(text);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Transcription failed');
    }
    setProcessing(false);
  };

  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:3, verticalAlign:'middle' }}>
      <button type="button" title={title || (recording ? 'Stop recording' : 'Speak to fill this field')}
        onClick={() => (recording ? handleStop() : handleStart())} disabled={processing}
        style={{
          width:24, height:24, borderRadius:'50%', border:'none', padding:0, flexShrink:0,
          cursor: processing ? 'wait' : 'pointer', fontSize:12, lineHeight:1,
          display:'flex', alignItems:'center', justifyContent:'center',
          background: recording ? '#DC2626' : '#F3F4F6', color: recording ? '#fff' : '#6B7280',
          animation: recording ? 'mic-pulse 1s ease-in-out infinite' : 'none',
        }}>
        {processing ? '⏳' : recording ? '⏹' : '🎙'}
      </button>
      {error && <span style={{ fontSize:10, color:'#DC2626' }} title={error}>⚠</span>}
      <style>{`@keyframes mic-pulse { 0%,100% { opacity:1 } 50% { opacity:.45 } }`}</style>
    </span>
  );
}
